<script setup lang="ts">
import { Dialog, Button } from 'primevue';
import { ref, watch } from 'vue';
import { useDeleteVehicle } from '../api/deleteVehicle';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import type { ProblemDetails } from 'shared/problems/domain/problemDetails';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useVehicleStore } from 'shared/vehicles/stores/vehicleStore';
import { Assert } from 'shared/assert';

defineExpose({
  show
});

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const vehicleId = ref<string>();
const vehicleName = ref<string>('');
const visible = ref(false);
const loading = ref(false);
const deleteVehicle = useDeleteVehicle();
const problems = ref<ProblemDetails>();
const vehicleStore = useVehicleStore();

watch(vehicleId, (newVehicleId) => {
  if (!newVehicleId) {
    visible.value = false;
  }
});

async function show(id: string) {
  try {
    loading.value = true;
    problems.value = undefined;
    const vehicle = vehicleStore.getById(id);
    Assert.definied(vehicle);
    vehicleId.value = vehicle.id;
    vehicleName.value = vehicle.name;
    visible.value = true;
  } finally {
    loading.value = false;
  }
}

async function executeDelete() {
  const id = vehicleId.value;
  if (!id) throw new Error();
  try {
    loading.value = true;
    const response = await deleteVehicle.execute({ vehicleId: id});

    if (response.ok) {
      vehicleId.value = undefined;
      visible.value = false;
      problems.value = undefined;
    } else {
      problems.value = response.details;
    }

  } finally {
    loading.value = false;
  }
}

function onHide() {
  vehicleId.value = undefined;
  visible.value = false;
}

</script>

<template>
  <Dialog modal @hide="onHide" :header="translations.vehicles.deleteVehicle(vehicleName)" v-model:visible="visible">
    <ProblemDetailsView v-model:model-value="problems"></ProblemDetailsView>
    <div class="flex items-center gap-4">
      <i class="pi pi-exclamation-triangle !text-3xl" style="color: var(--p-red-400)"></i>
      <span>{{ translations.vehicles.confirmDeleteMsg }}</span>
    </div>
    <template #footer>
      <Button :label="translations.vehicles.deleteBtnTxt" icon="pi pi-check" @click="executeDelete"></Button>
      <Button :label="translations.generic.cancel" icon="pi pi-times" text @click="onHide"></Button>
    </template>
  </Dialog>
</template>
